import clsx from "clsx";
import { motion } from "framer-motion";

import { useChromeContext } from "@components/chrome/hooks";
import { useClearBudgetMixUrlState } from "@features/mmm/hooks/mmm-url-state";
import { formatDate } from "@features/mmm/utils/formatters";
import { useFeatureFlags } from "@hooks/http";
import { type MMMClientCycleBase } from "@north-beam/nb-common";
import { CommonTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientCycles } from "@pages/mmm/hooks/use-client-cycles";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { MMMCycleDropdown, MMMCycleStage } from "./top-bar";

export const MINIMIZED_TOP_BAR_TAB_INFO_PORTAL_ID =
  "minimized-top-bar-tab-info-portal-id";

interface Props {
  isVisible: boolean;
}

export const MinimizedTopBar = ({ isVisible }: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.ALL_TABS);
  const { selectedCycle, setSelectedCycle } = useSelectedCycle();
  const [clientCycles] = useClientCycles();
  const { hasFeatureFlag } = useFeatureFlags();
  const { isSidebarExpanded } = useChromeContext();

  const clearMMMUrlState = useClearBudgetMixUrlState();

  const handleSelectCycle = (cycle: MMMClientCycleBase) => {
    trackMMMEvent({
      component: CommonTrackedComponent.CYCLE_SELECTOR,
      action: "onChange",
    });

    clearMMMUrlState();
    setSelectedCycle(cycle);
  };

  const positionTop = hasFeatureFlag("enableDashboardV2") ? 76 : 56;
  const sidebarWidth = hasFeatureFlag("enableDashboardV2")
    ? isSidebarExpanded
      ? 240
      : 73
    : 56;

  return (
    <motion.div
      key="minimized-top-bar"
      initial={{ translateY: -positionTop }}
      exit={{ translateY: -positionTop }}
      animate={{ translateY: isVisible ? 0 : -76 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "bg-v2-primary border-b border-v2-primary py-1 px-2 flex items-center justify-between fixed z-30 text-v2-primary",
      )}
      style={{ top: positionTop, width: `calc(100% - ${sidebarWidth}px)` }}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold mx-0 mt-0.5 mb-0  truncate">
            {formatDate(selectedCycle.date_range.start_date)} -{" "}
            {formatDate(selectedCycle.date_range.end_date)}{" "}
            {selectedCycle.name ? `(${selectedCycle.name})` : ""}
          </h3>
          <MMMCycleDropdown
            buttonSize="sm"
            selectedCycle={selectedCycle}
            onSelectCycle={handleSelectCycle}
            cycles={clientCycles}
          />
          <div id={MINIMIZED_TOP_BAR_TAB_INFO_PORTAL_ID}></div>
        </div>
        <div className="self-center mr-5">
          <MMMCycleStage cycle={selectedCycle} />
        </div>
      </div>
    </motion.div>
  );
};
